import React from 'react';

import type { LucideIcon } from 'lucide-react';
import { CalendarDays, CircleDot, Files, Repeat2, Timer } from 'lucide-react';
import { TaskStatusBadge } from 'src/components/workflow/workflow-detail/side-panel/task-review/TaskStatusPill';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { getTaskReviewDetailValues } from './task-page-helpers';
import { TaskPageCard } from './TaskPageCard';

const useStyles = makeStyles()(theme => ({
  icon: {
    color: theme.appColors.iconsTypeMuted,
    flexShrink: 0,
  },
  label: {
    color: theme.appColors.iconsType,
    flex: 1,
    fontSize: 14,
    lineHeight: '20px',
    minWidth: 0,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    padding: 0,
  },
  row: {
    '&:last-child': {
      borderBottom: 'none',
    },
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.ruleSubtle}`,
    display: 'flex',
    gap: theme.spacing(1),
    listStyle: 'none',
    minHeight: 44,
  },
  value: {
    color: theme.appColors.strongType,
    flexShrink: 0,
    fontSize: 14,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    lineHeight: '20px',
    textAlign: 'right',
  },
}));

type ReviewDetailsTask = Pick<Apollo.Task, 'completedAt' | 'round' | 'startedAt' | 'status'> & {
  assignments: ReadonlyArray<{ reviewRequestedAt: string | null }>;
};

interface TaskReviewDetailsProps {
  documentCount: number;
  task: ReviewDetailsTask;
}

/** The task page's sidebar: where the review stands at a glance. */
export function TaskReviewDetails({ documentCount, task }: TaskReviewDetailsProps): JSX.Element {
  const { classes } = useStyles();
  const { requested, round, runtime } = getTaskReviewDetailValues(task);

  return (
    <TaskPageCard title="Review details">
      <ul className={classes.list}>
        <DetailRow icon={CircleDot} label="Status">
          <TaskStatusBadge status={task.status} />
        </DetailRow>
        <DetailRow icon={CalendarDays} label="Requested">
          {requested ?? '—'}
        </DetailRow>
        <DetailRow icon={Timer} label="Runtime">
          {runtime ?? '—'}
        </DetailRow>
        <DetailRow icon={Repeat2} label="Review round">
          {round}
        </DetailRow>
        <DetailRow icon={Files} label="Documents">
          {documentCount}
        </DetailRow>
      </ul>
    </TaskPageCard>
  );
}

/** One line: the icon and label on the left, the value right-aligned. */
function DetailRow({
  children,
  icon: Icon,
  label,
}: {
  children: React.ReactNode;
  icon: LucideIcon;
  label: string;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <li className={classes.row}>
      <Icon className={classes.icon} size={16} />
      <span className={classes.label}>{label}</span>
      <span className={classes.value}>{children}</span>
    </li>
  );
}
